import { useEffect, useState, type ReactNode } from "react";
import { Pills, Switch } from "../vault/Common";
import { listChildren } from "../../features/vault/vaultApi";
import {
  getScopeSettings,
  saveScopeSettings,
  getEffectiveSettings,
  type RenderScope,
  type RenderValues,
} from "../../features/rendersettings/renderSettingsApi";

// ── Scope tree node (built from real Drive folders/files) ────────────────────
type Kind = "global" | "folder" | "subfolder" | "file";
interface Node {
  id: string;
  ind: number;
  scope: RenderScope;
  scopeId: string | null;
  name: string;
  parentName: string;
  /** scopeId of the folder above this node (null = directly under Global). Drives the inherited-value lookup. */
  parentScopeId: string | null;
  kind: Kind;
}
const GLOBAL: Node = { id: "global", ind: 0, scope: "global", scopeId: null, name: "", parentName: "", parentScopeId: null, kind: "global" };

// Default value for every control — matches the reference's initial appearance.
const DEF = {
  resolution: "4K",
  format: "PNG",
  transparentBg: false,
  turntable: true,
  turntableFrames: "60 frames",
  samples: 1024,
  aiDenoise: true,
  rayBounces: 12,
  bloom: true,
  environment: "Jewelry White — softbox ring",
  background: "#fff",
  groundShadow: true,
  groundShadowType: "Soft",
  cameraAngles: ["Perspective ¾", "Top", "Side"] as string[],
  defaultMetal: "18k Yellow",
  metalFinish: 8,
  stonePreset: "Diamond — D color, IF (dispersion 0.044)",
  autoVariants: true,
  watermark: true,
  watermarkPos: "Bottom-right",
  filenamePattern: "{sku}_{metal}_{angle}_{res}",
};

// Folder names flow into dangerouslySetInnerHTML — escape at that trust boundary.
const esc = (s: string) =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);

function noteFor(n: Node): string {
  if (n.kind === "global")
    return "<b>Global scope.</b> These are the workspace-wide defaults — every folder and model starts from here.";
  if (n.kind === "subfolder")
    return `<b>Subfolder: ${esc(n.parentName)} › ${esc(n.name)}.</b> Overrides here apply to this subfolder and the models inside it; everything else is inherited.`;
  if (n.kind === "folder")
    return `<b>Folder: ${esc(n.name)}.</b> Overrides here apply to this folder and everything inside it; everything else is inherited from Global.`;
  return `<b>Model: ${esc(n.name)}.</b> Inherits from ${esc(n.parentName)}; overrides here apply to just this model.`;
}

function Setting({ title, extra, inh, children }: { title: ReactNode; extra?: ReactNode; inh: string; children?: ReactNode }) {
  return (
    <div className="setting">
      <div className="s-top"><b>{title}</b>{extra}<span className={'inh-badge' + (inh === 'ovr' ? ' ovr' : '')}>{inh === 'ovr' ? 'Overridden here' : inh}</span></div>
      {children}
    </div>
  )
}

export default function RenderSettingsPage() {
  const [tree, setTree] = useState<Node[] | null>(null);
  const [selId, setSelId] = useState("global");
  const [values, setValues] = useState<RenderValues>({}); // this scope's stored OVERRIDES only
  // `saved` is written but never read — see if the dirty-check it was for
  // still exists before removing setSaved too.
  const [, setSaved] = useState<RenderValues>({});
  const [inherited, setInherited] = useState<RenderValues>({}); // parent-resolved values (file scope)
  const [overridden, setOverridden] = useState<Set<string>>(new Set()); // node ids known to have overrides
  const [busy, setBusy] = useState(false);

  const node = (tree ?? [GLOBAL]).find((n) => n.id === selId) ?? GLOBAL;

  // Build the scope tree from real Drive folders/files (global → folders → one level of subfolders/files).
  // ponytail: N+1 listChildren (one per top-level folder). Fine for real workspaces; batch if folder count explodes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const nodes: Node[] = [GLOBAL];
      try {
        const root = await listChildren(null);
        for (const folder of root.folders) {
          nodes.push({ id: "f:" + folder._id, ind: 1, scope: "folder", scopeId: folder._id, name: folder.name, parentName: "Global", parentScopeId: null, kind: "folder" });
          let child: { folders: typeof root.folders; models: typeof root.models } = { folders: [], models: [] };
          try { child = await listChildren(folder._id); } catch { /* keep folder as a leaf */ }
          for (const sub of child.folders)
            nodes.push({ id: "f:" + sub._id, ind: 2, scope: "folder", scopeId: sub._id, name: sub.name, parentName: folder.name, parentScopeId: folder._id, kind: "subfolder" });
          for (const m of child.models)
            nodes.push({ id: "m:" + m._id, ind: 2, scope: "file", scopeId: m._id, name: m.name, parentName: folder.name, parentScopeId: folder._id, kind: "file" });
        }
        for (const m of root.models)
          nodes.push({ id: "m:" + m._id, ind: 1, scope: "file", scopeId: m._id, name: m.name, parentName: "Global", parentScopeId: null, kind: "file" });
      } catch { /* keep just Global */ }
      if (!cancelled) setTree(nodes);
    })();
    return () => { cancelled = true; };
  }, []);

  // Hydrate all controls when the selected scope changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await getScopeSettings(node.scope, node.scopeId).catch(() => ({} as RenderValues));
      let inh: RenderValues = {};
      if (node.scope === "file" && node.scopeId) {
        inh = await getEffectiveSettings(node.scopeId).catch(() => ({} as RenderValues));
      } else if (node.scope === "folder") {
        // Folders/subfolders inherit down the chain (Global → parent folder). There's no
        // folder-effective endpoint, so merge the raw scopes here (most-specific wins),
        // mirroring the server's resolution order. Without this, non-overridden folder
        // controls showed hardcoded DEFAULTS while the badge claimed "Inherited · …".
        const [g, p] = await Promise.all([
          getScopeSettings("global", null).catch(() => ({} as RenderValues)),
          node.parentScopeId
            ? getScopeSettings("folder", node.parentScopeId).catch(() => ({} as RenderValues))
            : Promise.resolve({} as RenderValues),
        ]);
        inh = { ...g, ...p };
      }
      if (cancelled) return;
      setValues(stored);
      setSaved(stored);
      setInherited(inh);
      setOverridden((prev) => {
        const s = new Set(prev);
        if (Object.keys(stored).length) s.add(node.id); else s.delete(node.id);
        return s;
      });
    })();
    return () => { cancelled = true; };
  }, [node.id, node.scope, node.scopeId, node.parentScopeId]);

  // Display value: this scope's override → inherited (file scope) → default.
  const val = <T,>(k: string, d: T): T =>
    k in values ? (values[k] as T) : k in inherited ? (inherited[k] as T) : d;
  const set = (k: string, v: unknown) => setValues((p) => ({ ...p, [k]: v }));

  const inhLabel = node.scope === "global" ? "Set here" : "Inherited · " + node.parentName;
  const badge = (k: string) => (node.scope === "global" ? "Set here" : k in values ? "ovr" : inhLabel);

  const reload = async () => {
    const s = await getScopeSettings(node.scope, node.scopeId).catch(() => ({} as RenderValues));
    setValues(s); setSaved(s);
  };
  const markOverride = (has: boolean) =>
    setOverridden((prev) => { const s = new Set(prev); has ? s.add(node.id) : s.delete(node.id); return s; });
  const onSave = async () => {
    setBusy(true);
    try { await saveScopeSettings(node.scope, node.scopeId, values); setSaved(values); markOverride(Object.keys(values).length > 0); }
    finally { setBusy(false); }
  };
  const onReset = async () => {
    setBusy(true);
    try {
      await saveScopeSettings(node.scope, node.scopeId, {});
      setValues({}); setSaved({}); markOverride(false);
      // File scope's `inherited` is the effective (parent-resolved) settings, which
      // still contained this file's just-removed overrides — refresh it so controls
      // fall back to the true inherited value instead of showing the reset override
      // still labelled "Inherited". (Folder inherited excludes its own values.)
      if (node.scope === "file" && node.scopeId)
        setInherited(await getEffectiveSettings(node.scopeId).catch(() => ({} as RenderValues)));
    }
    finally { setBusy(false); }
  };
  const onDiscard = () => { void reload(); };

  const saveLabel = node.scope === "global" ? "Save global settings" : `Save for “${node.name}”`;

  const treeLabel = (n: Node): ReactNode => {
    if (n.kind === "global") return <>🌐 Everywhere (global) <span className="ovr badge gold" style={{ fontSize: 9.5 }}>Base</span></>;
    const dot = overridden.has(n.id) ? <span className="ovr rs-dot"></span> : null;
    return <>{n.kind === "file" ? "💍" : "📁"} {n.name} {dot}</>;
  };

  return (
    <>
      <div className="page-title">Render settings</div>
      <div className="page-sub">Set defaults once, everywhere — then override for any folder, subfolder or single model. The most specific scope always wins.</div>

      <div className="rs-layout" style={{ marginTop: 18 }}>
        <div className="rs-tree">
          <h4>Apply settings to…</h4>
          {tree === null ? (
            <button className="tree-item" disabled>Loading…</button>
          ) : (
            <>
              {tree.map((n) => (
                <button key={n.id} className={'tree-item' + (selId === n.id ? ' on' : '')} onClick={() => setSelId(n.id)}>
                  {n.ind > 0 && <span className={n.ind > 1 ? 'ind2' : 'ind'}></span>}{treeLabel(n)}
                </button>
              ))}
              {tree.length === 1 && (
                <div style={{ fontSize: 11.5, color: 'var(--muted)', padding: '4px 6px', lineHeight: 1.5 }}>
                  No folders yet — your global defaults apply everywhere.
                </div>
              )}
            </>
          )}
          <div style={{ borderTop: '1px solid var(--border)', margin: '10px 4px 8px' }}></div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)', padding: '0 6px', lineHeight: 1.5 }}>
            <span className="rs-dot" style={{ width: 7, height: 7 }}></span> = has overrides.<br />
            Resolution order: <b>Model → Subfolder → Folder → Global</b>.
          </div>
        </div>

        <div className="rs-panel">
          <div className="inherit-note">
            ⓘ <span dangerouslySetInnerHTML={{ __html: noteFor(node) }} />
            {node.scope !== 'global' && <button className="btn ghost" style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: 12 }} onClick={onReset} disabled={busy}>Reset to inherited</button>}
          </div>

          <div className="card">
            <h3>Output</h3>
            <div className="setting-grid">
              <Setting title="Resolution" inh={badge('resolution')}><Pills options={['1080p', '2K', '4K', '8K']} value={val('resolution', DEF.resolution)} onChange={(v) => set('resolution', v)} /></Setting>
              <Setting title="Format" inh={badge('format')}><Pills options={['PNG', 'JPG', 'WebP', 'TIFF']} value={val('format', DEF.format)} onChange={(v) => set('format', v)} /></Setting>
              <Setting title="Transparent background" inh={badge('transparentBg')} extra={<Switch key={`${selId}:transparentBg:${val('transparentBg', DEF.transparentBg)}`} on={val('transparentBg', DEF.transparentBg)} onChange={(v) => set('transparentBg', v)} />} />
              <Setting title="Turntable animation" inh={badge('turntable')} extra={<Switch key={`${selId}:turntable:${val('turntable', DEF.turntable)}`} on={val('turntable', DEF.turntable)} onChange={(v) => set('turntable', v)} />}><Pills options={['24 frames', '60 frames', '120 frames']} value={val('turntableFrames', DEF.turntableFrames)} onChange={(v) => set('turntableFrames', v)} /></Setting>
            </div>
          </div>
          <div className="card">
            <h3>Quality</h3>
            <div className="setting-grid">
              <Setting title="Samples" extra={<span className="code">{val('samples', DEF.samples)}</span>} inh={badge('samples')}><input type="range" min="64" max="4096" value={val('samples', DEF.samples)} onChange={(e) => set('samples', Number(e.target.value))} /></Setting>
              <Setting title="AI denoise" inh={badge('aiDenoise')} extra={<Switch key={`${selId}:aiDenoise:${val('aiDenoise', DEF.aiDenoise)}`} on={val('aiDenoise', DEF.aiDenoise)} onChange={(v) => set('aiDenoise', v)} />} />
              <Setting title="Ray bounces (caustics)" extra={<span className="code">{val('rayBounces', DEF.rayBounces)}</span>} inh={badge('rayBounces')}>
                <input type="range" min="2" max="24" value={val('rayBounces', DEF.rayBounces)} onChange={(e) => set('rayBounces', Number(e.target.value))} />
                <span className="hint">Higher = better fire &amp; brilliance in stones, slower renders.</span>
              </Setting>
              <Setting title="Bloom on highlights" inh={badge('bloom')} extra={<Switch key={`${selId}:bloom:${val('bloom', DEF.bloom)}`} on={val('bloom', DEF.bloom)} onChange={(v) => set('bloom', v)} />} />
            </div>
          </div>
          <div className="card">
            <h3>Scene &amp; lighting</h3>
            <div className="setting-grid">
              <Setting title="Environment (HDRI)" inh={badge('environment')}>
                <div className="field" style={{ marginTop: 0 }}><select value={val('environment', DEF.environment)} onChange={(e) => set('environment', e.target.value)}><option>Jewelry White — softbox ring</option><option>Studio Neutral</option><option>Warm boutique</option><option>Black velvet</option><option>Custom HDRI…</option></select></div>
              </Setting>
              <Setting title="Background" inh={badge('background')}>
                <div className="swatches">
                  {['#fff', '#F4EFE6', '#E9EDF2', '#211F1B'].map((c) => <div key={c} className={'swatch' + (val('background', DEF.background) === c ? ' on' : '')} style={{ background: c }} onClick={() => set('background', c)}></div>)}
                </div>
              </Setting>
              <Setting title="Ground shadow" inh={badge('groundShadow')} extra={<Switch key={`${selId}:groundShadow:${val('groundShadow', DEF.groundShadow)}`} on={val('groundShadow', DEF.groundShadow)} onChange={(v) => set('groundShadow', v)} />}><Pills options={['Soft', 'Contact', 'Reflection']} value={val('groundShadowType', DEF.groundShadowType)} onChange={(v) => set('groundShadowType', v)} /></Setting>
              <Setting title="Camera angles per model" inh={badge('cameraAngles')}><Pills multi options={['Perspective ¾', 'Top', 'Side', 'Macro (stone)', 'On-hand mockup']} value={val('cameraAngles', DEF.cameraAngles)} onChange={(v) => set('cameraAngles', v)} /></Setting>
            </div>
          </div>
          <div className="card">
            <h3>Materials</h3>
            <div className="setting-grid">
              <Setting title="Default metal" inh={badge('defaultMetal')}><Pills options={['18k Yellow', '18k Rose', '14k White', 'Platinum', 'Silver 925']} value={val('defaultMetal', DEF.defaultMetal)} onChange={(v) => set('defaultMetal', v)} /></Setting>
              <Setting title="Metal finish" extra={<span className="code">roughness {(Number(val('metalFinish', DEF.metalFinish)) / 100).toFixed(2)}</span>} inh={badge('metalFinish')}>
                <input type="range" min="0" max="100" value={val('metalFinish', DEF.metalFinish)} onChange={(e) => set('metalFinish', Number(e.target.value))} /><span className="hint">Mirror polish ← → brushed / matte</span>
              </Setting>
              <Setting title="Stone preset" inh={badge('stonePreset')}>
                <div className="field" style={{ marginTop: 0 }}><select value={val('stonePreset', DEF.stonePreset)} onChange={(e) => set('stonePreset', e.target.value)}><option>Diamond — D color, IF (dispersion 0.044)</option><option>Emerald (Zambian)</option><option>Ruby (Burmese)</option><option>Blue sapphire</option></select></div>
              </Setting>
              <Setting title="Auto-generate metal variants" inh={badge('autoVariants')} extra={<Switch key={`${selId}:autoVariants:${val('autoVariants', DEF.autoVariants)}`} on={val('autoVariants', DEF.autoVariants)} onChange={(v) => set('autoVariants', v)} />}>
                <span className="hint">One upload → renders in yellow, rose, white &amp; platinum automatically.</span>
              </Setting>
            </div>
          </div>
          <div className="card">
            <h3>Watermark &amp; delivery</h3>
            <div className="setting-grid">
              <Setting title="Watermark renders" inh={badge('watermark')} extra={<Switch key={`${selId}:watermark:${val('watermark', DEF.watermark)}`} on={val('watermark', DEF.watermark)} onChange={(v) => set('watermark', v)} />}><Pills options={['Center', 'Bottom-right', 'Tiled']} value={val('watermarkPos', DEF.watermarkPos)} onChange={(v) => set('watermarkPos', v)} /></Setting>
              <Setting title="Filename pattern" inh={badge('filenamePattern')}>
                <div className="field" style={{ marginTop: 0 }}><input type="text" value={val('filenamePattern', DEF.filenamePattern)} onChange={(e) => set('filenamePattern', e.target.value)} /></div>
              </Setting>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn ghost" onClick={onDiscard} disabled={busy}>Discard changes</button>
            <button className="btn primary" onClick={onSave} disabled={busy}>{saveLabel}</button>
          </div>
        </div>
      </div>
    </>
  )
}
